import { useCallback, useEffect } from 'react';
import {
  listPoolShippings,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
} from '../../modules/api';
import { Backdrop } from '../../modules/design-system';
import { useInfinitePagination } from '../../modules/pagination';
import { useShippingDisabled } from '../../modules/shipping';
import { useBackHandler } from '../../modules/backStack';
import { DeliveriesTab } from '../MyPacks/tabs/DeliveriesTab';
import { DeliveryNewPage, DeliveryShowPage } from '../Delivery';
import { ShippingHeader } from './ShippingHeader';
import { useShippingRoute } from './shippingRoute';

const PER_PAGE = 30;

// Standalone Shipping starts a delivery with no pre-selected cards — the user
// picks items inside DeliveryNewPage's ItemPickerModal. A module-level const
// keeps the prop reference stable across renders.
const EMPTY_ITEMS: IframePoolOrderItem[] = [];

export interface ShippingPageProps {
  client: ApiClient;
}

/**
 * Standalone `/shipping` page — the My Packs "Shipping" tab lifted into its own
 * route. Renders the deliveries list (`DeliveriesTab`) under a single-purpose
 * header (title + "Add Delivery"), with no Home button and no tab switcher.
 * Delivery create/show screens are reached via the shared delivery sub-router
 * rooted at `/shipping`.
 */
export function ShippingPage({ client }: ShippingPageProps) {
  const { route, push: navigate } = useShippingRoute();
  const disableShipping = useShippingDisabled();

  // Deliveries feed — lives at page level (above the delivery-new/show
  // early returns) so the list survives the sub-route round-trips and
  // DeliveriesTab stays presentational, mirroring MyPacksPage.
  const shippingsFetcher = useCallback(
    (page: number, per: number) =>
      listPoolShippings(client, { page, per }).then((res) => ({
        items: res.shippings,
        totalCount: res.meta.totalCount,
      })),
    [client],
  );
  const deliveries = useInfinitePagination<IframePoolShipping>(shippingsFetcher, {
    per: PER_PAGE,
  });

  // Block deep-links to the create-delivery route when shipping is disabled.
  useEffect(() => {
    if (disableShipping && route.kind === 'delivery-new') {
      navigate({ kind: 'index' });
    }
  }, [disableShipping, route.kind, navigate]);

  // Host back button (modules/backStack): a delivery sub-route steps back to
  // the index, same as its own Back button.
  useBackHandler(route.kind !== 'index', () => navigate({ kind: 'index' }), 'subroute');

  if (route.kind === 'delivery-new' && !disableShipping) {
    return (
      <DeliveryNewPage
        client={client}
        selectedItems={EMPTY_ITEMS}
        onBack={() => navigate({ kind: 'index' })}
        // Back to the list — the feed lives at page level (no remount), so
        // refetch explicitly to surface the freshly created delivery.
        onSuccess={() => {
          void deliveries.refetch();
          navigate({ kind: 'index' });
        }}
      />
    );
  }

  if (route.kind === 'delivery-show') {
    return (
      <DeliveryShowPage
        client={client}
        shippingId={route.id}
        onBack={() => navigate({ kind: 'index' })}
      />
    );
  }

  return (
    <div data-page="my-packs">
      <Backdrop bgImageUrl={null} />
      {/* Optional photo layer above the gradient backdrop, mirroring
          MyPacksPage so the Design Constructor `bgImage` knob applies here
          too. Resolves to `none` when unset. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className="@container relative flex min-h-screen w-full flex-col"
      >
        <ShippingHeader
          {...(!disableShipping && { onAddDelivery: () => navigate({ kind: 'delivery-new' }) })}
        />
        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
          <main className="flex-1 px-4 pb-12 @[600px]:px-6">
            <DeliveriesTab
              shippings={deliveries.items}
              initialLoading={deliveries.initialLoading}
              loadingMore={deliveries.loadingMore}
              reachedEnd={deliveries.reachedEnd}
              error={deliveries.error}
              onRetry={() => void deliveries.refetch()}
              sentinelRef={deliveries.sentinelRef}
              onOpenDelivery={(id) => navigate({ kind: 'delivery-show', id })}
            />
          </main>
        </div>
      </div>
    </div>
  );
}
